import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { RELEASE_THEME_IDS, type ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../common/types';
import { getFullAPIFileURL } from '../../../common/getFullAPIFileURL';
import { releaseThemeMessages } from '../../../music24/promote/themes/releaseThemeMessages';
import { useCoverEditor } from '../../CoverEditorContext';
import { coverEditorMessages } from '../../coverEditorMessages';
import { bankMoods, filterBank } from './bankFilters';
import { PictureTile } from './PictureTile';
import { usePickPicture } from './usePickPicture';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Bank tab (design cover-editor c3): the published pictures of the cover bank, free on every
 * plan (owner decision D6), with a filter by release theme and by mood. The theme of the song is
 * the first filter.
 */
const BankTabComponent: React.FC = () => {
  const intl = useIntl();
  const { backgrounds, data, state, readOnly } = useCoverEditor();
  const pick = usePickPicture();
  const [themeId, setThemeId] = useState<ReleaseThemeId | null>(data.song.themeId);
  const [mood, setMood] = useState<string | null>(null);
  const moods = useMemo(() => bankMoods(backgrounds), [backgrounds]);
  const shown = useMemo(() => filterBank(backgrounds, themeId, mood), [backgrounds, mood, themeId]);
  const { background } = state.doc;
  const currentUuid =
    background.kind === 'picture' && background.source.kind === 'bank' ? background.source.backgroundUuid : null;

  if (backgrounds.length === 0) {
    return <span className={styles.hint}>{intl.formatMessage(coverEditorMessages.bankEmpty)}</span>;
  }
  return (
    <>
      <div className={styles.chips} role="group" aria-label={intl.formatMessage(coverEditorMessages.bankThemes)}>
        <button
          type="button"
          className={clsx(styles.chip, themeId === null && styles.chipOn)}
          aria-pressed={themeId === null}
          onClick={() => setThemeId(null)}
        >
          {intl.formatMessage(coverEditorMessages.bankAll)}
        </button>
        {RELEASE_THEME_IDS.map((id) => (
          <button
            key={id}
            type="button"
            className={clsx(styles.chip, themeId === id && styles.chipOn)}
            aria-pressed={themeId === id}
            onClick={() => setThemeId(id)}
          >
            {intl.formatMessage(releaseThemeMessages[id].name)}
          </button>
        ))}
      </div>
      {moods.length > 0 && (
        <div className={styles.chips} role="group" aria-label={intl.formatMessage(coverEditorMessages.bankMoods)}>
          {moods.map((item) => (
            <button
              key={item}
              type="button"
              className={clsx(styles.chip, mood === item && styles.chipOn)}
              aria-pressed={mood === item}
              onClick={() => setMood(mood === item ? null : item)}
            >
              {item}
            </button>
          ))}
        </div>
      )}
      <div className={clsx(styles.grid3, styles.scroll)}>
        {shown.map((item, index) => (
          <PictureTile
            key={item.uuid}
            thumbUrl={getFullAPIFileURL(item.thumbUrl)}
            label={intl.formatMessage(coverEditorMessages.bankPictureLabel, { number: index + 1 })}
            isOn={item.uuid === currentUuid}
            disabled={readOnly}
            onPick={() => pick({ kind: 'bank', backgroundUuid: item.uuid }, getFullAPIFileURL(item.url), item.palette)}
          />
        ))}
      </div>
      {shown.length === 0 && <span className={styles.hint}>{intl.formatMessage(coverEditorMessages.bankNoMatch)}</span>}
      <span className={styles.hint}>
        {intl.formatMessage(coverEditorMessages.bankHint, { count: backgrounds.length })}
      </span>
    </>
  );
};

export const BankTab = TypedMemo(BankTabComponent);
